import React, { useMemo } from "react";
import { Select } from "@/components/Selects/select";
import { SingleValue } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { SELECT_CLASS_NAMES } from "@/components/Selects/select-class-names";
import { cn } from "@/lib/utils";

type OptionType = { label: string; value: number };
type GroupedOption = { label: string; options: OptionType[] };

/**
 * Single-select autocomplete for `user_credentials_file_type`. Lists every
 * credential type (Driver's License, DEA, state licenses, etc.) grouped by
 * category. Used by the hiring-process template editor to set the
 * `requestedCredentialFileTypeId` hint on provider document-upload items.
 */
export function CredentialFileTypeSelect({
  value,
  onChange,
  disabled = false,
  placeholder = "Search credential types…",
}: {
  value?: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const trpc = useTRPC();
  const { data, isLoading } = useQuery(
    trpc.credentialFiles.getFileTypes.queryOptions(),
  );

  const grouped = useMemo<GroupedOption[]>(() => {
    if (!data) {
      return [];
    }
    const byCategory = new Map<string, OptionType[]>();
    for (const t of data) {
      const cat = t.category ?? "Other";
      const list = byCategory.get(cat) ?? [];
      list.push({ label: t.name, value: t.id });
      byCategory.set(cat, list);
    }
    return Array.from(byCategory.entries())
      .map(([cat, options]) => ({
        label: cat,
        options: options.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const flatOptions = useMemo(
    () => grouped.flatMap((g) => g.options),
    [grouped],
  );

  const selected = useMemo(
    () => flatOptions.find((o) => o.value === value) ?? null,
    [flatOptions, value],
  );

  const handleChange = (opt: SingleValue<OptionType>) => {
    onChange(opt?.value ?? null);
  };

  return (
    <Select<OptionType, false, GroupedOption>
      value={selected}
      options={grouped}
      onChange={handleChange}
      isDisabled={disabled}
      isLoading={isLoading}
      isClearable
      placeholder={placeholder}
      classNames={{
        control: (state) => cn(SELECT_CLASS_NAMES.control(state), "!min-h-8"),
        valueContainer: () => cn(SELECT_CLASS_NAMES.valueContainer(), "!py-0"),
      }}
    />
  );
}
